import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  PdfDownloadOutputSchema,
  SortDirectionSchema,
  tablePdfColumnsSchema,
  thenSortSchema,
} from "../../schemas/common";

/**
 * AR Tasks page: the billing tasks the AR detector opens (shipped, not
 * invoiced and not sent), with the metrics above the table. Gated by
 * the `ar-tasks-enabled` flag; every procedure refuses when it is off.
 * Assigning reuses `tasks.batchAssign`; sending reuses `arInvoiceEmail`.
 */

export const AR_TASKS_TABS = ["all", "to_invoice", "to_send"] as const;
/**
 * Days-pending buckets on the AR Tasks page. A task is late after 5 days
 * (Pennrose AR's threshold), so the first bucket ends there.
 */
export const AR_TASKS_AGING_BUCKETS = ["0-5", "6-14", "15+"] as const;

/**
 * @deprecated The buckets from before the 5-day rule. The API still accepts
 * them as a filter and returns their counts so older clients keep working;
 * the page uses `AR_TASKS_AGING_BUCKETS`.
 */
export const LEGACY_AR_TASKS_AGING_BUCKETS = ["0-2", "3-7", "8-14"] as const;

/** Rows-per-page choices on the AR Tasks page; the last is the most a list call returns. */
export const AR_TASKS_PAGE_SIZES = [25, 50, 100, 150] as const;
export const AR_TASKS_MAX_PAGE_SIZE = 150;
export const AR_TASKS_CLOSE_OUTCOMES = [
  "sent_outside_gently",
  "invoiced_elsewhere",
  "no_charge",
  "duplicate",
] as const;

/**
 * Close outcomes that make sense for each kind of task: an unsent invoice
 * can't be "invoiced elsewhere", and a shipped order with no invoice
 * can't be "sent outside Gently". Both allow no charge and duplicate.
 */
export const AR_TASKS_CLOSE_OUTCOMES_BY_KIND = {
  to_invoice: ["invoiced_elsewhere", "no_charge", "duplicate"],
  to_send: ["sent_outside_gently", "no_charge", "duplicate"],
} as const satisfies Record<
  "to_invoice" | "to_send",
  readonly (typeof AR_TASKS_CLOSE_OUTCOMES)[number][]
>;

/** Outcomes valid for every kind in the selection (empty selection: none). */
export function arTasksCloseOutcomesFor(
  kinds: ReadonlyArray<keyof typeof AR_TASKS_CLOSE_OUTCOMES_BY_KIND>,
): ArTasksCloseOutcome[] {
  if (kinds.length === 0) return [];
  return AR_TASKS_CLOSE_OUTCOMES.filter((outcome) =>
    kinds.every((kind) => {
      const allowed: readonly string[] = AR_TASKS_CLOSE_OUTCOMES_BY_KIND[kind];
      return allowed.includes(outcome);
    }),
  );
}

/**
 * The task type behind each AR task kind; the tab's feed opens tasks of
 * these types.
 */
export const AR_TASK_TYPE_KEY_BY_KIND = {
  to_invoice: "ar-shipped-not-invoiced",
  to_send: "ar-invoice-not-sent",
} as const;

/**
 * How a customer gets invoices, from the feed's `channels` input. `unknown`
 * is a customer too new to tell; a not-sent task without the input is email.
 */
export const AR_TASK_CHANNELS = ["email", "edi", "portal", "unknown"] as const;

export const ArTasksTabSchema = z.enum(AR_TASKS_TABS);
export const ArTaskChannelSchema = z.enum(AR_TASK_CHANNELS);
export const ArTasksAgingBucketSchema = z.enum(AR_TASKS_AGING_BUCKETS);
/** Any bucket the list filter accepts: current, or a deprecated one. */
export const ArTasksListAgingBucketSchema = z.enum([
  ...AR_TASKS_AGING_BUCKETS,
  ...LEGACY_AR_TASKS_AGING_BUCKETS,
]);
export const ArTasksCloseOutcomeSchema = z.enum(AR_TASKS_CLOSE_OUTCOMES);

export const ArTasksKindSchema = z.enum(["to_invoice", "to_send"]);

/** How the list is grouped: `assignee` orders it by assignee, Unassigned last. */
export const ArTasksGroupBySchema = z.enum(["assignee"]);

/**
 * Columns the list sorts by. Each value is the table's column id, so a
 * header click round-trips through the URL unchanged.
 */
export const ArTasksSortBySchema = z.enum([
  "reference",
  "date",
  "amount",
  "customer",
  "issue",
  "pending",
  "channel",
  "task",
  "assignee",
  "latestNote",
]);

export const ListArTasksInputSchema = z.object({
  tab: ArTasksTabSchema.default("all"),
  search: z.string().trim().max(200).optional(),
  assignedToMe: z.boolean().optional(),
  agingBucket: ArTasksListAgingBucketSchema.optional(),
  /** ISO dates (YYYY-MM-DD), inclusive, on the ship / invoice date. */
  dateFrom: z.iso.date().optional(),
  dateTo: z.iso.date().optional(),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(AR_TASKS_MAX_PAGE_SIZE).default(50),
  /**
   * Group the list by assignee: by assignee name with Unassigned last, then
   * days pending, so a group's tasks stay together across pages.
   */
  groupBy: ArTasksGroupBySchema.optional(),
  /**
   * Column to sort by; most days pending first when unset. Grouped by
   * assignee, it sorts within each group.
   */
  sortBy: ArTasksSortBySchema.optional(),
  sortDirection: SortDirectionSchema.optional(),
  /** Tie-breaking sort levels applied after `sortBy`. */
  thenSort: thenSortSchema(ArTasksSortBySchema).optional(),
});

export const ArTaskRowSchema = z.object({
  taskId: z.uuid(),
  displayId: z.string().nullable(),
  kind: ArTasksKindSchema,
  status: z.string(),
  /**
   * The sales order (to_invoice) or invoice (to_send) the task is about;
   * null until that record syncs into Gently (its feed opens the task first).
   */
  anchorId: z.uuid().nullable(),
  reference: z.string().nullable(),
  /** Ship date (to_invoice) or invoice date (to_send), YYYY-MM-DD. */
  date: z.string().nullable(),
  amount: z.string().nullable(),
  customerId: z.uuid().nullable(),
  customerName: z.string().nullable(),
  daysPending: z.number().int().nullable(),
  /**
   * @deprecated Use `channels`. The same channels as one label ("Email,
   * Portal"); null for to_invoice. Kept so clients built before `channels`
   * keep working.
   */
  channel: z.string().nullable(),
  /** Channels the customer gets invoices on (to_send); empty for to_invoice. */
  channels: z.array(ArTaskChannelSchema),
  /** Newest AR note on the invoice in the ERP, copied onto the task by its feed. */
  invoiceNote: z
    .object({
      body: z.string(),
      /** YYYY-MM-DD. */
      date: z.string().nullable(),
    })
    .nullable(),
  assigneeType: z.enum(["user", "agent"]).nullable(),
  assigneeUserId: z.uuid().nullable(),
  assigneeAgentDefinitionId: z.uuid().nullable(),
  assigneeName: z.string().nullable(),
  latestNote: z
    .object({
      body: z.string(),
      actorName: z.string().nullable(),
      occurredAt: z.string(),
    })
    .nullable(),
});

/** An assignee group on the page, with its count across every page. */
export const ArTasksAssigneeGroupSchema = z.object({
  /** `arTaskAssigneeKey` of the group's rows; null for Unassigned. */
  key: z.string().nullable(),
  /** The user's or agent's name; null for Unassigned. */
  name: z.string().nullable(),
  /** Tasks in the group matching the filters, on every page. */
  count: z.number().int(),
  /** Their total amount, on every page (decimal string). */
  amount: z.string(),
});

export const ListArTasksResponseSchema = z.object({
  rows: z.array(ArTaskRowSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  /** The page's assignee groups, in row order; only with `groupBy: "assignee"`. */
  assigneeGroups: z.array(ArTasksAssigneeGroupSchema).optional(),
});

const AmountCountSchema = z.object({
  count: z.number().int(),
  amount: z.string(),
});

export const ArTasksSummaryResponseSchema = z.object({
  toInvoice: AmountCountSchema,
  toSend: AmountCountSchema,
  aging: z.object({
    "0-5": z.number().int(),
    "6-14": z.number().int(),
    "15+": z.number().int(),
    /** @deprecated Pre-5-day bucket, kept for older clients. */
    "0-2": z.number().int(),
    /** @deprecated Pre-5-day bucket, kept for older clients. */
    "3-7": z.number().int(),
    /** @deprecated Pre-5-day bucket, kept for older clients. */
    "8-14": z.number().int(),
  }),
  unassigned: z.number().int(),
  /** When AR data last synced from the ERP (the tasks refresh after it); null if never. */
  lastSyncedAt: z.iso.datetime().nullable(),
  /** Null until the detector has run once for the org. */
  taskTypeIds: z.object({
    toInvoice: z.uuid().nullable(),
    toSend: z.uuid().nullable(),
  }),
});

export const CloseArTasksInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(AR_TASKS_MAX_PAGE_SIZE),
  outcome: ArTasksCloseOutcomeSchema,
  note: z.string().trim().max(2000).optional(),
});

export const CloseArTasksResponseSchema = z.object({
  closed: z.number().int(),
  skipped: z.number().int(),
});

/** Column ids the AR Tasks PDF can render: the table's data columns. */
export const ArTaskPdfColumnIdSchema = z.enum([
  "reference",
  "date",
  "amount",
  "customer",
  "issue",
  "pending",
  "channel",
  "task",
  "assignee",
  "latestNote",
]);

/**
 * Download the selected tasks as a letterhead PDF. `columns` mirrors the
 * caller's visible table layout (id order, display labels, relative widths)
 * so the document matches the table on screen. Row order follows `taskIds`.
 * Capped at the list's max page size, so a full page can be downloaded.
 */
export const DownloadArTasksPdfInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(AR_TASKS_MAX_PAGE_SIZE),
  columns: tablePdfColumnsSchema(ArTaskPdfColumnIdSchema),
});

export const DownloadArTasksPdfOutputSchema = PdfDownloadOutputSchema;

export const GetArTaskStatusHistoryInputSchema = z.object({
  taskId: z.uuid(),
});

/**
 * One field change from the ERP's order change log (Famous
 * `AR_TRX_HEADER_HIST`), e.g. Status: Ship TO Invoice. `at` is the ERP's
 * local wall-clock time, with no zone.
 */
export const ArTaskStatusChangeSchema = z.object({
  /** The row's sequence in the order's log; unique per order. */
  seq: z.number().int(),
  /** The field's label in the ERP, e.g. "Status" or "Invoice Date". */
  field: z.string(),
  /** Null when the field was empty. */
  from: z.string().nullable(),
  to: z.string().nullable(),
  /** The ERP user who made the change; null when unknown. */
  by: z.string().nullable(),
  at: z.iso.datetime({ local: true }),
});

export const ArTaskStatusHistoryResponseSchema = z.object({
  /** False when the ERP can't be reached or the task's record isn't linked to it. */
  available: z.boolean(),
  /** Newest first. */
  entries: z.array(ArTaskStatusChangeSchema),
  /**
   * When the order was created in the ERP, the ERP's local wall-clock time
   * with no zone: the log has no entry for it. Null when unknown.
   */
  createdAt: z.iso.datetime({ local: true }).nullable().optional(),
});

export const arTasksContracts = {
  summary: oc
    .input(z.object({}).optional())
    .output(ArTasksSummaryResponseSchema)
    .route({
      method: "GET",
      summary: "AR Tasks metrics",
      description:
        "Open billing-task counts and dollars (shipped not invoiced, not sent), aging buckets on days pending, and the unassigned count.",
    }),
  list: oc
    .input(ListArTasksInputSchema)
    .output(ListArTasksResponseSchema)
    .route({
      method: "GET",
      summary: "List open AR billing tasks",
      description:
        "Open shipped-not-invoiced and saved-not-sent tasks with their sales order or invoice, customer, days pending, assignee and latest note.",
    }),
  close: oc
    .input(CloseArTasksInputSchema)
    .output(CloseArTasksResponseSchema)
    .route({
      method: "POST",
      summary: "Close AR billing tasks",
      description:
        "Completes up to 150 open AR billing tasks with a manual outcome and optional note. Tasks that are not open AR billing tasks are skipped.",
    }),
  downloadPdf: oc
    .input(DownloadArTasksPdfInputSchema)
    .output(DownloadArTasksPdfOutputSchema)
    .route({
      method: "POST",
      summary: "Download AR tasks as PDF",
      description:
        "Generates a letterhead PDF of up to 150 selected open AR billing tasks using the caller's column layout; returns base64 PDF bytes and filename.",
    }),
  statusHistory: oc
    .input(GetArTaskStatusHistoryInputSchema)
    .output(ArTaskStatusHistoryResponseSchema)
    .route({
      method: "GET",
      summary: "AR task order change log",
      description:
        "The ERP's change log for the task's sales order or invoice (status, invoice date, terms, credit hold), newest first, read live from the ERP.",
    }),
} as const;

export type ArTasksTab = z.infer<typeof ArTasksTabSchema>;
export type ArTasksAgingBucket = z.infer<typeof ArTasksAgingBucketSchema>;
export type ArTasksListAgingBucket = z.infer<
  typeof ArTasksListAgingBucketSchema
>;
export type ArTaskChannel = z.infer<typeof ArTaskChannelSchema>;
export type ArTasksCloseOutcome = z.infer<typeof ArTasksCloseOutcomeSchema>;
export type ListArTasksInput = z.infer<typeof ListArTasksInputSchema>;
export type ArTasksSortBy = z.infer<typeof ArTasksSortBySchema>;
export type ArTaskRow = z.infer<typeof ArTaskRowSchema>;
export type ArTasksAssigneeGroup = z.infer<typeof ArTasksAssigneeGroupSchema>;
export type ListArTasksResponse = z.infer<typeof ListArTasksResponseSchema>;
export type ArTasksSummaryResponse = z.infer<
  typeof ArTasksSummaryResponseSchema
>;
export type CloseArTasksInput = z.infer<typeof CloseArTasksInputSchema>;
export type ArTaskPdfColumnId = z.infer<typeof ArTaskPdfColumnIdSchema>;
export type DownloadArTasksPdfInput = z.infer<
  typeof DownloadArTasksPdfInputSchema
>;
export type DownloadArTasksPdfOutput = z.infer<
  typeof DownloadArTasksPdfOutputSchema
>;
export type ArTaskStatusChange = z.infer<typeof ArTaskStatusChangeSchema>;
export type ArTaskStatusHistoryResponse = z.infer<
  typeof ArTaskStatusHistoryResponseSchema
>;

/** A row's assignee group key (`user:<id>` / `agent:<id>`); null when unassigned. */
export function arTaskAssigneeKey(
  row: Pick<ArTaskRow, "assigneeUserId" | "assigneeAgentDefinitionId">,
): string | null {
  if (row.assigneeUserId) return `user:${row.assigneeUserId}`;
  if (row.assigneeAgentDefinitionId)
    return `agent:${row.assigneeAgentDefinitionId}`;
  return null;
}
